import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import { AuthContext } from "../../../context/AuthContext";
import DateTimeInputFormik from "../../../components/UI/DateTimeInput/DateTimeInputFormik";
import SelectUf from "../../../components/UI/SelectInput/SelectUf";
import SelectMunicipio from "../../../components/UI/SelectInput/SelectMunicipio";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";

const FormExpedienteAtipico = ({ history }) => {
  const { isAllowed } = useContext(AuthContext);
  const [expediente, setExpediente] = useState({});
  const [atualizarDados, setAtualizarDados] = useState(false);
  const [dataAtual, setDataAtual] = useState("");
  const [dadosInfo, setDadosInfo] = useState({});

  const {
    request: consultaRequest,
    data: consultaData,
    errors: consultaErrors,
    isLoading: consultaIsLoading,
  } = useApiClient();
  const { request: submitRequest, data: submitData, errors: submitErrors, isLoading: submitIsLoading } = useApiClient();
  const {
    request: excluirRequest,
    data: excluirData,
    errors: excluirErrors,
    isLoading: excluirIsLoading,
  } = useApiClient();

  const validationSchema = Yup.object().shape({
    sequencialPontoFacultativo: Yup.string().default(""),
    dataPontoFacultativo: Yup.date().required("Informe a data do expediente atípico"),
    descricaoPontoFacultativo: Yup.string()
      .default("")
      .ensure()
      .required("Informe a Descrição")
      .max(1000, "A descrição não pode ter mais de 1000 caracteres"),
  });

  const initialValues = {
    sequencialPontoFacultativo: "",
    dataPontoFacultativo: history?.location?.state?.dataPontoFacultativo,
    descricaoPontoFacultativo: "",
    sequencialUf: "20",
    sequencialMunicipio: "98",
  };

  useEffect(() => {
    if (history?.location?.state) {
      setExpediente(history.location.state);
    }
  }, [expediente, history]);

  useEffect(() => {
    if (expediente.sequencialPontoFacultativo) {
      consultaRequest(apiRoutes.fiscalizacao.parametrizacao.consultaExpedienteAtipico, {id: expediente.sequencialPontoFacultativo,});
      setAtualizarDados(true);
    }
  }, [expediente, consultaRequest]);

  const handleSubmit = useCallback(
    (values) => {
      setDataAtual(values.dataPontoFacultativo);
      if (values.sequencialPontoFacultativo)
        submitRequest(apiRoutes.fiscalizacao.parametrizacao.alteraExpedienteAtipico, values);
      else submitRequest(apiRoutes.fiscalizacao.parametrizacao.incluiExpedienteAtipico, values);
    },
    [submitRequest],
  );

  const handleDelete = (sequencial) => {
    if (sequencial) {
      excluirRequest(apiRoutes.fiscalizacao.parametrizacao.excluiExpedienteAtipico, {sequencialPontoFacultativo: sequencial,});
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting } = formik;

  useEffect(() => {
    if ((!consultaData && !consultaErrors) || consultaIsLoading) return;

    if (consultaData && consultaData?.result && atualizarDados) {
      const info = {
        codigo: consultaData?.result?.sequencialPontoFacultativo,
        data: {
          documentoInclusao: consultaData?.result?.usrInclusao,
          nomeFormalInclusao: consultaData?.result?.nmFormalUsuario,
          nomeFormalAlteracao: consultaData?.result?.nmFormalUsuarioAlt,
          documentoAlteracao: consultaData?.result?.usrAlteracao,
          dataInclusao: consultaData?.result?.tmInclusao,
          dataAlteracao: consultaData?.result?.tmAlteracao,
        },
      };

      formik.setValues(consultaData.result);
      setAtualizarDados(false);
      setDadosInfo(info);
    }

    if (consultaErrors) {
      toast.error(consultaErrors.message);
    }
  }, [formik, consultaData, consultaErrors, consultaIsLoading, atualizarDados]);

  useEffect(() => {
    if (excluirIsLoading) return;

    if (excluirData || excluirErrors) setSubmitting(false);

    if (excluirData) {
      toast.success("Expediente atípico excluído com sucesso");
      history.push("/app/expedienteatipico");
    }
  }, [excluirData, excluirErrors, excluirIsLoading, history, setSubmitting]);

  useEffect(() => {
    if (submitIsLoading) return;

    if (submitData || submitErrors) setSubmitting(false);

    if (submitData) {
      toast.success("Expediente atípico salvo com sucesso");
      history.push({
        pathname: "/app/expedienteatipico",
        state: { atualizada: false },
      });
    }
  }, [setSubmitting, submitData, submitErrors, submitIsLoading, history, dataAtual]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Expedientes Atípicos</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {expediente.sequencialPontoFacultativo
                      ? `Alteração do Expediente Atípico nº ${expediente.sequencialPontoFacultativo}`
                      : "Novo Expediente Atípico"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} />
                </Col>
              </Row>
            </div>

            <div className="card-body">
              <FormAlert formik={formik} data={submitData} errors={submitErrors} isLoading={submitIsLoading} />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sequencialPontoFacultativo
                      ? !isAllowed(funcionalidades.expedienteAtipico.alterar)
                      : !isAllowed(funcionalidades.expedienteAtipico.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col}>
                      <Form.Label>Data:</Form.Label>
                      <div className="form-inline">
                        <DateTimeInputFormik formik={formik} field="dataPontoFacultativo" />
                      </div>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Label>UF:</Form.Label>
                      <div className="form-inline">
                        <SelectUf formik={formik} field="sequencialUf" disabled />
                      </div>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Label>Município:</Form.Label>
                      <div className="form-inline">
                        <SelectMunicipio formik={formik} field="sequencialMunicipio" update disabled />
                      </div>
                    </Form.Group>
                    <Form.Group as={Col} md={5}>
                      <Form.Label>Descrição</Form.Label>
                      <TextInputFormik formik={formik} field="descricaoPontoFacultativo" maxLength="1000" />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  {formik.isSubmitting ? (
                    <TextSpinner text="Processando..." />
                  ) : (
                    <ButtonSubmitFormikPermission
                      permission={
                        formik.values.sequencialPontoFacultativo
                          ? funcionalidades.expedienteAtipico.alterar
                          : funcionalidades.expedienteAtipico.inserir
                      }
                      disabled={formik.isSubmitting}
                      name="Salvar"
                    >
                      Salvar
                    </ButtonSubmitFormikPermission>
                  )}
                  {"    "}
                  {!formik.isSubmitting && formik.values.sequencialPontoFacultativo && (
                    <Button
                      variant="primary"
                      className="mr-3"
                      onClick={() => handleDelete(formik.values.sequencialPontoFacultativo)}
                    >
                      Excluir
                    </Button>
                  )}
                  {"    "}
                  {!formik.isSubmitting && !formik.values.sequencialPontoFacultativo && (
                    <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                      Limpar
                    </Button>
                  )}
                  {"    "}
                  {!formik.isSubmitting && (
                    <Button variant="danger" onClick={() => history.goBack()}>
                      Cancelar
                    </Button>
                  )}
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormExpedienteAtipico;
